import { Group, Stack, Text, useMantineTheme } from "@mantine/core"
import type { MantineTheme } from "@mantine/core"
import {
  IconArrowsSort,
  IconCirclePlus,
  IconSortAscending,
  IconSortDescending,
  IconX,
} from "@tabler/icons-react"
import _ from "lodash"
import type { Dispatch, ReactNode, SetStateAction } from "react"
import { useMemo, useState } from "react"

import { ActionIcon } from "../../atoms/ActionIcons/ActionIcon"
import { CriteriaBuilderPopover } from "../CriteriaBuilderPopover/CriteriaBuilderPopover"
import { DragNDrop } from "../Inputs/DragNDrop/DragNDrop"
import { Select } from "../Inputs/Select/Select"

export type SortDirection = "asc" | "desc"

export interface SortValue {
  readonly value: SortDirection
  readonly rank: number
}

export type Sorts = Record<string, SortValue>

export interface SortBuilderLabels {
  readonly title: string
  readonly searchPlaceholder: string
  readonly ascending: string
  readonly descending: string
}

export interface SortBuilderProps<T> {
  readonly options: {
    readonly label: string
    readonly value: keyof T
  }[]
  readonly sorts: Sorts
  readonly setSorts: Dispatch<SetStateAction<Sorts>>
  readonly labels: SortBuilderLabels
}

const getSortButtonLabel = (
  sorts: Sorts,
  getLabel: (key: string) => string,
  labels: SortBuilderLabels,
  sortDirections: Record<SortDirection, { label: string; icon: ReactNode }>,
  theme: MantineTheme
): { label: string; icon: ReactNode } => {
  const sortKeys = Object.keys(sorts)

  if (sortKeys.length === 1) {
    const [key, value] = Object.entries(sorts)[0]
    return {
      label: getLabel(key),
      icon: sortDirections[value.value]?.icon ?? null,
    }
  }

  if (sortKeys.length > 1) {
    return {
      label: `${labels.title} (${sortKeys.length})`,
      icon: (
        <IconArrowsSort
          stroke={theme.other.icon.stroke}
          size={theme.other.icon.size}
        />
      ),
    }
  }

  return {
    label: labels.title,
    icon: (
      <IconArrowsSort
        stroke={theme.other.icon.stroke}
        size={theme.other.icon.size}
      />
    ),
  }
}

export const SortBuilder = <T extends Record<string, SortValue>>({
  options,
  sorts,
  setSorts,
  labels,
}: SortBuilderProps<T>) => {
  const theme = useMantineTheme()
  const sortDirections: Record<
    SortDirection,
    { label: string; icon: ReactNode }
  > = {
    asc: {
      label: labels.ascending,
      icon: (
        <IconSortAscending
          stroke={theme.other.icon.stroke}
          size={theme.other.icon.size}
        />
      ),
    },
    desc: {
      label: labels.descending,
      icon: (
        <IconSortDescending
          stroke={theme.other.icon.stroke}
          size={theme.other.icon.size}
        />
      ),
    },
  }

  const [search, setSearch] = useState<string>("")

  const getLabel = (key: string): string => {
    return options.find((option) => option.value === key)?.label ?? key
  }

  const filteredOptions = useMemo(
    () =>
      _.orderBy(
        options.filter(
          (option) =>
            option.label.toLowerCase().includes(search.toLowerCase()) &&
            !Object.keys(sorts).includes(option.value as string)
        ),
        "label"
      ),
    [search, options, sorts]
  )

  const { label, icon } = getSortButtonLabel(
    sorts,
    getLabel,
    labels,
    sortDirections,
    theme
  )

  return (
    <CriteriaBuilderPopover
      buttonLabel={label}
      buttonIcon={icon}
      isActive={Object.keys(sorts).length > 0}
      searchPlaceholder={labels.searchPlaceholder}
      searchValue={search}
      onSearchChange={setSearch}
      hasActiveItems={Object.keys(sorts).length > 0}
      activeItems={
        <DragNDrop
          onDragEnd={({ item, destination }) => {
            if (!destination || destination.index === null) return
            setSorts((prev) => {
              const newSorts = { ...prev }

              newSorts[item.id] = {
                ...newSorts[item.id],
                rank: destination.index,
              }
              for (const [key, value] of Object.entries(newSorts)) {
                if (value.rank >= destination.index && key !== item.id) {
                  newSorts[key] = {
                    ...value,
                    rank: value.rank + 1,
                  }
                }
              }
              return newSorts
            })
          }}
          data={_.orderBy(Object.entries(sorts), (sort) => sort[1].rank).map(
            ([key, value]) => ({
              id: key,
              label: getLabel(key),
              value,
              cta: (
                <Group>
                  <Select
                    value={value.value}
                    data={Object.keys(sortDirections).map((directionKey) => ({
                      label:
                        sortDirections[directionKey as SortDirection].label,
                      value: directionKey,
                    }))}
                    onChange={(selectedDirection) => {
                      if (selectedDirection === null) return
                      setSorts((prev) => ({
                        ...prev,
                        [key]: {
                          value: selectedDirection as SortDirection,
                          rank: value.rank,
                        },
                      }))
                    }}
                    w={150}
                    comboboxProps={{
                      withinPortal: false,
                    }}
                  />
                  <ActionIcon
                    onClick={() => {
                      setSorts((prev) => {
                        const newSorts = { ...prev }
                        const rank = newSorts[key].rank
                        delete newSorts[key]
                        for (const [key2, value2] of Object.entries(newSorts)) {
                          if (value2.rank > rank) {
                            newSorts[key2] = {
                              ...value2,
                              rank: value2.rank - 1,
                            }
                          }
                        }
                        return newSorts
                      })
                    }}
                    icon={<IconX />}
                    variant="tertiary"
                  />
                </Group>
              ),
              itemProps: {
                bd: 0,
                m: 0,
                px: 0,
                py: 2,
                display: "flex",
                flexWrap: "nowrap",
              },
            })
          )}
        />
      }
      availableItems={
        <Stack gap={theme.spacing.xxs}>
          {filteredOptions.map((option) => {
            return (
              <Group key={option.value as string} justify="space-between">
                <Text>{option.label}</Text>
                <ActionIcon
                  onClick={() => {
                    setSorts((prev) => ({
                      ...prev,
                      [option.value as string]: {
                        value: "asc",
                        rank: Object.keys(sorts).length,
                      },
                    }))
                  }}
                  variant="tertiary"
                  icon={<IconCirclePlus />}
                />
              </Group>
            )
          })}
        </Stack>
      }
    />
  )
}
